// src/pages/RestockPage.js
import React, { useState, useEffect } from 'react';
import { getProducts, getStores } from '../api/apiService';

const bs = { fontFamily: "'Montserrat', sans-serif" };
const ds = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

const RestockPage = () => {
  const [products,        setProducts]        = useState([]);
  const [stores,          setStores]          = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [restockList,     setRestockList]     = useState([]);
  const [loading,         setLoading]         = useState(true);

  useEffect(() => {
    (async () => {
      const [prods, sts] = await Promise.all([getProducts(), getStores()]);
      setProducts(prods);
      setStores(sts);
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (loading) return;
    const items = [];
    products.forEach(product => {
      product.variants.forEach(variant => {
        const stocks    = variant.storeStocks || {};
        const threshold = product.lowStockThreshold ?? 5;

        const check = (storeId) => {
          const stock = stocks[storeId] || 0;
          if (stock <= threshold) {
            items.push({
              productId:       product.id,
              productName:     product.name,
              variantInfo:     `${variant.size} / ${variant.color}`,
              currentStock:    stock,
              threshold,
              storeId,
              storeName:       stores.find(s => String(s.id) === String(storeId))?.name || 'N/A',
              quantityToOrder: Math.max(0, threshold * 2 - stock),
            });
          }
        };

        if (selectedStoreId) {
          check(selectedStoreId);
        } else {
          const toCheck = product.assignedStores?.length
            ? product.assignedStores
            : stores.map(s => String(s.id));
          toCheck.forEach(check);
        }
      });
    });
    setRestockList(items);
  }, [products, selectedStoreId, stores, loading]);

  const handleQtyChange = (index, val) => {
    const updated = [...restockList];
    updated[index].quantityToOrder = Math.max(0, Number(val));
    setRestockList(updated);
  };

  const removeItem = (index) => setRestockList(restockList.filter((_, i) => i !== index));

  const downloadCSV = () => {
    if (!restockList.length) return;
    let csv = 'Producto,Variante,Tienda,Stock Actual,Umbral,Cantidad a Pedir\n';
    restockList.forEach(item => {
      csv += `${item.productName},${item.variantInfo},${item.storeName},${item.currentStock},${item.threshold},${item.quantityToOrder}\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `reabastecimiento_${selectedStoreId || 'todas'}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div style={{ padding: '24px 16px', maxWidth: '1200px', margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ ...ds, fontSize: '2rem', fontWeight: 400, color: '#060D14', margin: '0 0 4px' }}>
            Reabastecimiento
          </h1>
          <p style={{ ...bs, fontSize: '12px', color: '#bbb', margin: 0 }}>
            {restockList.length} producto{restockList.length !== 1 ? 's' : ''} con stock bajo
          </p>
        </div>
        <button onClick={downloadCSV} disabled={!restockList.length}
          style={{ ...bs, display: 'flex', alignItems: 'center', gap: '8px',
            background: restockList.length ? '#961914' : '#e5e5e5',
            color: restockList.length ? 'white' : '#bbb',
            border: 'none', padding: '10px 20px', cursor: restockList.length ? 'pointer' : 'not-allowed',
            fontSize: '10px', fontWeight: 600, letterSpacing: '0.15em',
            textTransform: 'uppercase', borderRadius: '2px', transition: 'background 0.2s',
          }}
          onMouseOver={e => { if (restockList.length) e.currentTarget.style.background = '#7a140f'; }}
          onMouseOut={e => { if (restockList.length) e.currentTarget.style.background = '#961914'; }}>
          <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
          </svg>
          Descargar CSV
        </button>
      </div>

      {/* Filtro tienda */}
      <div style={{ background: 'white', border: '1px solid #f0eaea',
        padding: '16px 20px', borderRadius: '2px', marginBottom: '16px' }}>
        <label style={{ ...bs, display: 'block', fontSize: '10px', letterSpacing: '0.18em',
          textTransform: 'uppercase', color: '#999', marginBottom: '6px' }}>
          Filtrar por tienda
        </label>
        <select value={selectedStoreId} onChange={e => setSelectedStoreId(e.target.value)}
          style={{ ...bs, padding: '9px 12px', border: '1px solid #e5e0e0',
            fontSize: '12px', color: '#060D14', outline: 'none', borderRadius: '2px',
            background: 'white', minWidth: '200px' }}
          onFocus={e => e.target.style.borderColor = '#961914'}
          onBlur={e => e.target.style.borderColor = '#e5e0e0'}>
          <option value="">Todas las tiendas</option>
          {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      {/* Tabla */}
      <div style={{ background: 'white', border: '1px solid #f0eaea',
        borderRadius: '2px', overflow: 'hidden' }}>

        <div style={{ display: 'grid',
          gridTemplateColumns: '1fr 140px 140px 80px 80px 100px 80px',
          padding: '10px 20px', background: '#faf9f9',
          borderBottom: '1px solid #f0eaea' }}>
          {['Producto', 'Variante', 'Tienda', 'Stock', 'Umbral', 'A pedir', ''].map((h, i) => (
            <span key={i} style={{ ...bs, fontSize: '10px', fontWeight: 600,
              letterSpacing: '0.15em', textTransform: 'uppercase', color: '#bbb' }}>
              {h}
            </span>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center' }}>
            <p style={{ ...bs, fontSize: '12px', color: '#bbb' }}>Analizando inventario…</p>
          </div>
        ) : restockList.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center' }}>
            <p style={{ fontSize: '28px', marginBottom: '8px' }}>✓</p>
            <p style={{ ...bs, fontSize: '12px', color: '#22c55e', fontWeight: 600 }}>
              Todo el inventario está por encima del umbral
            </p>
          </div>
        ) : (
          restockList.map((item, idx) => (
            <div key={idx} style={{
              display: 'grid',
              gridTemplateColumns: '1fr 140px 140px 80px 80px 100px 80px',
              padding: '12px 20px', alignItems: 'center',
              borderBottom: idx < restockList.length - 1 ? '1px solid #faf5f5' : 'none',
              background: idx % 2 === 0 ? 'white' : '#fdfafa',
              transition: 'background 0.15s',
            }}
            onMouseOver={e => e.currentTarget.style.background = '#fdf5f5'}
            onMouseOut={e => e.currentTarget.style.background = idx % 2 === 0 ? 'white' : '#fdfafa'}>

              <span style={{ ...ds, fontSize: '1rem', fontWeight: 600, color: '#060D14',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {item.productName}
              </span>
              <span style={{ ...bs, fontSize: '11px', color: '#888' }}>{item.variantInfo}</span>
              <span style={{ ...bs, fontSize: '11px', color: '#888' }}>{item.storeName}</span>
              <span style={{ ...bs, fontSize: '13px', fontWeight: 600, color: '#dc2626' }}>
                {item.currentStock}
              </span>
              <span style={{ ...bs, fontSize: '12px', color: '#bbb' }}>{item.threshold}</span>

              {/* Input cantidad */}
              <input type="number" value={item.quantityToOrder} min="0"
                onChange={e => handleQtyChange(idx, e.target.value)}
                style={{ ...bs, width: '72px', padding: '6px 10px', textAlign: 'center',
                  border: '1px solid #e5e0e0', fontSize: '12px', color: '#060D14',
                  outline: 'none', borderRadius: '2px',
                }}
                onFocus={e => e.target.style.borderColor = '#961914'}
                onBlur={e => e.target.style.borderColor = '#e5e0e0'} />

              <button onClick={() => removeItem(idx)} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#ddd', padding: '4px', transition: 'color 0.15s',
              }}
              onMouseOver={e => e.currentTarget.style.color = '#dc2626'}
              onMouseOut={e => e.currentTarget.style.color = '#ddd'}>
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RestockPage;
